/**
 * @graphql agg
 */
import { useCallback } from 'react';

import { useQueryInterop } from '@confluence/apollo-relay-interop';
import { useWriteFragmentInterop } from '@confluence/apollo-relay-interop/entry-points/useWriteFragmentInterop';
import { useReadFragmentInterop } from '@confluence/apollo-relay-interop/entry-points/useReadFragmentInterop';

import gql from 'graphql-tag';

import { useContentType } from '@confluence/page-context';
import type { PageStateType } from '@confluence/page-context';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError';
import { containsNetworkError } from '@confluence/network';
import { setContentErrorAttributes } from '@confluence/monitoring/entry-points/error-attributes';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';

// Import types for the viewed comments query
import type {
	useViewedCommentsQuery,
	useViewedCommentsQueryVariables,
	ConfluenceAnalyticsCommentContentType,
} from './__types__/useViewedCommentsQuery';

const ContentAnalyticsViewedComments = gql`
	query useViewedCommentsQuery(
		$contentId: ID!
		$contentType: ConfluenceAnalyticsCommentContentType!
	) {
		contentAnalyticsViewedComments(contentId: $contentId, contentType: $contentType) {
			commentIds
		}
	}
`;

export interface UseViewedCommentsResult {
	getViewedCommentsCacheOnly: () => {
		success: boolean;
		data?: Set<string>;
		error?: Error;
	};
	getViewedCommentsFresh: () => Promise<{ success: boolean; data?: Set<string>; error?: Error }>;
	updateViewedCommentsCache: (commentIds: string[]) => void;
}

// Type for the ViewedComments fragment data
interface ViewedCommentsFragmentData {
	commentIds: string[];
	__typename: 'ViewedComments';
}

// Helper function to safely convert content type for the viewed comments query
const getValidContentTypeForViewedComments = (
	contentType: PageStateType['contentType'],
): ConfluenceAnalyticsCommentContentType => {
	// Only page, database, and whiteboard support the viewed comments query
	if (contentType === 'page' || contentType === 'database' || contentType === 'whiteboard') {
		return contentType as ConfluenceAnalyticsCommentContentType;
	}
	// Fallback to page for other content types (blogpost, embed, etc.)
	return 'page' as ConfluenceAnalyticsCommentContentType;
};

export const useViewedComments = ({ pageId }: { pageId: string }): UseViewedCommentsResult => {
	const [contentType] = useContentType();
	const writeFragmentInterop = useWriteFragmentInterop<ViewedCommentsFragmentData>({
		fragmentSpec: gql`
			fragment ViewedCommentsFragment on ViewedComments {
				commentIds
			}
		`,
	});

	const readFragmentInterop = useReadFragmentInterop<ViewedCommentsFragmentData>({
		fragmentSpec: gql`
			fragment ViewedCommentsFragment on ViewedComments {
				commentIds
			}
		`,
	});

	// Network-only query for fresh data
	const { refetch } = useQueryInterop<useViewedCommentsQuery, useViewedCommentsQueryVariables>(
		/* @ts-expect-error -- http://go/cfe-use-relay-artifacts */
		ContentAnalyticsViewedComments,
		{
			skip: true, // Never query on render
			fetchPolicy: 'network-only',
			variables: {
				contentId: pageId,
				contentType: getValidContentTypeForViewedComments(contentType),
			},
		},
	);

	// Helper function to process the query result
	const processQueryResult = useCallback((data: useViewedCommentsQuery) => {
		const commentIds = data?.contentAnalyticsViewedComments?.commentIds ?? [];
		const viewedCommentsSet = new Set<string>(
			commentIds.filter((id: string | null): id is string => id !== null),
		);

		return {
			success: true,
			data: viewedCommentsSet,
		};
	}, []);

	// Helper function for cache-only queries
	const executeCacheOnlyQuery = useCallback(() => {
		if (!pageId || !contentType) {
			throw new Error('Page ID and content type are required');
		}

		try {
			// Use readFragmentInterop to get fresh data from cache
			const fragmentId = `ViewedComments:${pageId}`;
			const data = readFragmentInterop({ id: fragmentId });

			if (data) {
				// Convert fragment data to query result format
				const queryResult: useViewedCommentsQuery = {
					contentAnalyticsViewedComments: {
						commentIds: data.commentIds,
						__typename: 'ViewedComments' as const,
					},
				};
				return processQueryResult(queryResult);
			} else {
				return {
					success: false,
					error: new Error('No viewed comments data found in cache'),
				};
			}
		} catch (error) {
			// Handle cache read errors - these are local operations so we just log unexpected errors
			setContentErrorAttributes(error, {
				objectId: pageId,
				objectType: 'page',
			});

			getMonitoringClient().submitError(error, {
				attribution: Attribution.COMMENTS,
			});

			markErrorAsHandled(error);
			return {
				success: false,
				error: error as Error,
			};
		}
	}, [pageId, contentType, processQueryResult, readFragmentInterop]);

	// Helper function for network-only queries
	const executeNetworkOnlyQuery = useCallback(async () => {
		if (!pageId) {
			throw new Error('Page ID and content type are required');
		}

		try {
			const result = await refetch({
				contentId: pageId,
				contentType: getValidContentTypeForViewedComments(contentType),
			});
			return processQueryResult(result.data);
		} catch (error) {
			// Handle expected errors (403/unauthorized, network errors) - just mark as handled
			if (isUnauthorizedError(error) || containsNetworkError(error)) {
				markErrorAsHandled(error);
				return {
					success: false,
					error: error as Error,
				};
			}

			// Handle unexpected errors
			setContentErrorAttributes(error, {
				objectId: pageId,
				objectType: 'page',
			});

			getMonitoringClient().submitError(error, {
				attribution: Attribution.COMMENTS,
			});

			markErrorAsHandled(error);
			return {
				success: false,
				error: error as Error,
			};
		}
	}, [pageId, contentType, processQueryResult, refetch]);

	// Cached data query - uses cache-only for performance
	const getViewedCommentsCacheOnly = useCallback(() => {
		try {
			return executeCacheOnlyQuery();
		} catch (error) {
			return {
				success: false,
				error: error as Error,
			};
		}
	}, [executeCacheOnlyQuery]);

	// Fresh data query - always uses network-only for latest data
	const getViewedCommentsFresh = useCallback(
		() => executeNetworkOnlyQuery(),
		[executeNetworkOnlyQuery],
	);

	// Function to update the viewed comments cache
	const updateViewedCommentsCache = useCallback(
		(commentIds: string[]) => {
			if (!pageId || !contentType || commentIds.length === 0) {
				return;
			}

			try {
				// Read existing cache data using readFragmentInterop
				const fragmentId = `ViewedComments:${pageId}`;
				const cacheData = readFragmentInterop({ id: fragmentId });

				if (!cacheData) {
					// Cache doesn't exist yet, create new cache entry
					writeFragmentInterop({
						fragmentId,
						data: {
							commentIds: commentIds,
							__typename: 'ViewedComments',
						},
					});
					return;
				}

				// Get existing comment IDs from cache
				const existingCommentIds = cacheData?.commentIds ?? [];
				const existingSet = new Set(existingCommentIds.filter((id): id is string => id !== null));

				// Add new comment IDs to the set
				commentIds.forEach((id) => existingSet.add(id));

				// Update the cache with the new data
				writeFragmentInterop({
					fragmentId,
					data: {
						commentIds: Array.from(existingSet),
						__typename: 'ViewedComments',
					},
				});
			} catch (error) {
				// Handle cache writing errors - these are local operations so we just log unexpected errors
				setContentErrorAttributes(error, {
					objectId: pageId,
					objectType: 'page',
				});

				getMonitoringClient().submitError(error, {
					attribution: Attribution.COMMENTS,
				});

				markErrorAsHandled(error);
			}
		},
		[pageId, contentType, readFragmentInterop, writeFragmentInterop],
	);

	return {
		getViewedCommentsCacheOnly,
		getViewedCommentsFresh,
		updateViewedCommentsCache,
	};
};
